/* Homeport: cart gifting and its paid wrap line use one mutation queue. */
(function (root) {
  'use strict';
  const GIFT_KEYS = ['Gift', 'Secret gift', 'Requested ship date', 'Gift wrap', 'Gift message', 'Monogram'];

  function createGiftCart(config, fetcher, publish = function () {}) {
    let queue = Promise.resolve();
    let pending = 0;
    let failure = null;
    let syncPromise = null;
    let syncAgain = false;
    const isWrap = (item) => Number(item.variant_id) === Number(config.wrapVariant);

    async function request(url, body) {
      const response = await fetcher(url, {
        method: body ? 'POST' : 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        ...(body ? { body: JSON.stringify(body) } : { cache: 'no-store' })
      });
      const data = await response.json();
      if (!response.ok || data.status >= 400) {
        throw new Error(typeof data.description === 'string' ? data.description : 'Your cart could not be updated. Please try again.');
      }
      return data;
    }
    const read = () => request(config.cartUrl);
    const change = (item, quantity, properties) => request(config.changeUrl, { id: item.key, quantity, ...(properties ? { properties } : {}) });

    async function reconcile(cart) {
      if (!config.wrapVariant) {
        if (cart.items.some((item) => item.properties && item.properties['Gift wrap'] === 'Yes')) {
          throw new Error('Gift wrapping is unavailable. Remove wrapping from your gift options before checkout.');
        }
        return cart;
      }
      const parents = cart.items.filter((item) => !isWrap(item) && item.properties && item.properties['Gift wrap'] === 'Yes');
      const label = parents.map((item) => item.product_title || item.title).join(', ');
      const wrapLines = cart.items.filter(isWrap);
      // Old carts may contain duplicate wrapping lines. Remove extras by current key.
      for (const extra of wrapLines.slice(1)) cart = await change(extra, 0);
      const wrap = cart.items.find(isWrap);
      if (!parents.length) return wrap ? change(wrap, 0) : cart;
      if (wrap && wrap.quantity === parents.length && (wrap.properties || {}).For === label) return cart;
      if (wrap) return change(wrap, parents.length, { ...(wrap.properties || {}), For: label });
      await request(config.addUrl, { items: [{ id: config.wrapVariant, quantity: parents.length, properties: { For: label } }] });
      return read();
    }

    function run(work) {
      pending++;
      const operation = queue.catch(function () {}).then(async () => {
        try {
          const cart = await work();
          failure = null;
          publish(cart);
          return cart;
        } catch (error) {
          failure = error;
          throw error;
        } finally { pending--; }
      });
      queue = operation;
      return operation;
    }

    return {
      get busy() { return pending > 0; },
      get error() { return failure; },
      read,
      sync() {
        if (syncPromise) syncAgain = true;
        else {
          syncPromise = run(async () => {
            let cart;
            do { syncAgain = false; cart = await reconcile(await read()); } while (syncAgain);
            return cart;
          }).finally(() => { syncPromise = null; });
        }
        return syncPromise;
      },
      save(key, fields, monogramEnabled, giftId) {
        return run(async () => {
          let cart = await read();
          const item = cart.items.find((line) => line.key === key || (giftId && (line.properties || {})._gift_options_id === giftId));
          if (!item || isWrap(item) || item.gift_card) throw new Error('This item has changed. Return to your cart and open its gift options again.');
          const previous = { ...(item.properties || {}) };
          const properties = { ...previous };
          GIFT_KEYS.forEach((name) => { delete properties[name]; });
          GIFT_KEYS.filter((name) => name !== 'Gift').forEach((name) => {
            if (name === 'Monogram' && !monogramEnabled) return;
            const value = String(fields[name] || '').trim();
            if (value) properties[name] = value;
          });
          if (['Secret gift', 'Requested ship date', 'Gift wrap', 'Gift message'].some((name) => properties[name])) properties.Gift = 'Yes';
          // Shopify cannot clear all properties with {}. A stable private ID also
          // identifies this line after a property edit changes its Shopify key.
          properties._gift_options_id = previous._gift_options_id || giftId || root.crypto.randomUUID();
          cart = await change(item, item.quantity, properties);
          try { return await reconcile(cart); }
          catch (error) {
            // A wrapping stock/API failure must not mark an unpaid item as wrapped.
            const current = cart.items.find((line) => (line.properties || {})._gift_options_id === properties._gift_options_id);
            if (current) {
              try {
                await change(current, current.quantity, { ...previous, _gift_options_id: properties._gift_options_id });
                const restored = await reconcile(await read());
                publish(restored);
              } catch (_) { /* Keep checkout blocked until a successful retry. */ }
            }
            throw new Error('Gift options were not saved. ' + error.message);
          }
        });
      }
    };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { createGiftCart };
  if (!root.document) return;
  const document = root.document;
  const configElement = document.getElementById('GiftCartConfig');
  const panel = document.querySelector('[data-gift-panel]');
  if (!configElement || !panel || root.homeportGiftCart) return;
  const config = JSON.parse(configElement.textContent);
  const notice = document.querySelector('[data-gift-notice]');
  const form = panel.querySelector('[data-gift-form]');
  const fields = panel.querySelector('[data-gift-fields]');
  const saveButton = panel.querySelector('[data-gift-save]');
  const errorElement = panel.querySelector('[data-gift-error]');
  let active = null;
  let saving = false;
  let returnGiftId = '';
  const home = panel.parentElement;

  function showNotice(message) {
    notice.querySelector('[data-gift-notice-text]').textContent = message;
    notice.hidden = !!active;
  }
  function publish(cart) {
    notice.hidden = true;
    document.dispatchEvent(new CustomEvent('theme:cart:change', { bubbles: true, detail: { cart, giftOptions: true } }));
    document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
  }
  const cartService = createGiftCart(config, root.fetch.bind(root), publish);
  root.homeportGiftCart = cartService;
  root.syncGiftWrap = () => cartService.sync();
  function synchronize() {
    return cartService.sync().catch((error) => { showNotice(error.message); });
  }
  document.addEventListener('theme:cart:change', (event) => {
    if (!event.detail || !event.detail.giftOptions) synchronize();
  });
  notice.querySelector('[data-gift-retry]').addEventListener('click', synchronize);

  function today() {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }
  function setSaving(value) {
    saving = value;
    fields.disabled = value;
    saveButton.disabled = value;
    panel.querySelectorAll('[data-gift-back], [data-gift-close]').forEach((button) => { button.disabled = value; });
    panel.setAttribute('aria-busy', String(value));
    saveButton.textContent = value ? 'Saving…' : 'Save gift options';
  }
  function closePanel(restore = true) {
    if (!active || saving) return;
    const old = active;
    active = null;
    panel.hidden = true;
    old.host.classList.remove('gift-panel-open');
    home.appendChild(panel);
    if (cartService.error) showNotice(cartService.error.message);
    if (old.drawer && old.drawer.classList.contains('drawer--visible')) {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true, detail: old.drawer.querySelector('[data-drawer-scrolls]') }));
    }
    if (restore) {
      const trigger = Array.from(old.host.querySelectorAll('[data-gift-edit]')).find((button) => button.dataset.giftId === old.giftId || button.dataset.lineKey === old.key);
      (trigger || old.host.querySelector('button, a'))?.focus({ preventScroll: true });
      if (!old.drawer) root.scrollTo(0, old.scrollY);
    }
  }
  function openPanel(trigger) {
    if (saving) return;
    const drawer = trigger.closest('[data-drawer="drawer-cart"]');
    const host = drawer ? drawer.querySelector('.drawer__content') : trigger.closest('.cart__template');
    if (!host) return;
    if (active) closePanel(false);
    let properties;
    try { properties = JSON.parse(trigger.dataset.lineProps || '{}') || {}; } catch (_) { properties = {}; }
    active = { host, drawer, key: trigger.dataset.lineKey, giftId: properties._gift_options_id || root.crypto.randomUUID(), monogram: trigger.dataset.monogramEnabled === 'true', scrollY: root.scrollY };
    notice.hidden = true;
    form.reset();
    errorElement.hidden = true;
    panel.querySelector('[data-gift-item-name]').textContent = `Gift options for ${trigger.dataset.lineTitle}`;
    panel.querySelector('[data-gift-monogram]').hidden = !active.monogram;
    panel.querySelector('[data-gift-field="Monogram"]').disabled = !active.monogram;
    panel.querySelector('[data-gift-field="Requested ship date"]').min = today();
    panel.querySelectorAll('[data-gift-field]').forEach((input) => {
      const value = properties[input.dataset.giftField] || '';
      if (input.type === 'checkbox') input.checked = value === 'Yes';
      else input.value = value;
    });
    const existingWrap = panel.querySelector('[data-gift-existing-wrap]');
    if (existingWrap) {
      existingWrap.hidden = properties['Gift wrap'] !== 'Yes';
      existingWrap.querySelector('input').disabled = existingWrap.hidden;
    }
    host.appendChild(panel);
    host.classList.add('gift-panel-open');
    panel.hidden = false;
    panel.querySelector('[data-gift-scroll]').scrollTop = 0;
    if (drawer) {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true, detail: panel.querySelector('[data-gift-scroll]') }));
    } else panel.scrollIntoView({ block: 'start' });
    panel.querySelector('[data-gift-back]').focus();
  }
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-gift-edit]');
    if (trigger) { event.preventDefault(); openPanel(trigger); }
  });
  panel.querySelector('[data-gift-back]').addEventListener('click', () => closePanel());
  panel.querySelector('[data-gift-close]').addEventListener('click', () => {
    const drawer = active && active.drawer;
    closePanel(!drawer);
    if (drawer) drawer.dispatchEvent(new CustomEvent('theme:drawer:close'));
  });
  // Capture both events: Pipeline closes the entire drawer on Escape keyup.
  ['keydown', 'keyup'].forEach((name) => document.addEventListener(name, (event) => {
    if (!active || event.key !== 'Escape') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (name === 'keyup') closePanel();
  }, true));
  document.addEventListener('click', (event) => {
    if (!active || !event.target.closest('[data-drawer-underlay]')) return;
    if (saving) { event.preventDefault(); event.stopImmediatePropagation(); }
    else closePanel(false);
  }, true);
  // Keep tab focus within the active second screen, including the full cart page.
  panel.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;
    const focusable = Array.from(panel.querySelectorAll('button, input, textarea')).filter((element) => !element.disabled && element.getClientRects().length);
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!active || saving) return;
    const date = panel.querySelector('[data-gift-field="Requested ship date"]');
    date.min = today();
    if (!form.reportValidity()) return;
    const values = {};
    panel.querySelectorAll('[data-gift-field]').forEach((input) => {
      if (!input.disabled) values[input.dataset.giftField] = input.type === 'checkbox' ? (input.checked ? 'Yes' : '') : input.value;
    });
    errorElement.hidden = true;
    setSaving(true);
    try {
      await cartService.save(active.key, values, active.monogram, active.giftId);
      returnGiftId = active.giftId;
      setSaving(false);
      closePanel();
    } catch (error) {
      setSaving(false);
      errorElement.textContent = error.message;
      errorElement.hidden = false;
      errorElement.focus();
      showNotice('Gift options need attention before checkout. Retry saving, or retry gift wrapping.');
    }
  });
  // Restore focus after Pipeline replaces the item markup and its old button.
  new MutationObserver(() => {
    if (!returnGiftId || active) return;
    const button = Array.from(document.querySelectorAll('[data-gift-edit]')).find((element) => element.dataset.giftId === returnGiftId);
    if (button && (document.activeElement === document.body || document.activeElement.matches('[data-gift-edit]'))) button.focus({ preventScroll: true });
  }).observe(document.body, { childList: true, subtree: true });
  // User input ends focus restoration; subsequent cart refreshes must not steal focus.
  ['pointerdown', 'keydown'].forEach((name) => document.addEventListener(name, () => {
    if (!saving) returnGiftId = '';
  }, true));
  function guardCheckout(event) {
    const checkout = event.type === 'submit' ? event.submitter?.matches('[name="checkout"]') : event.target.closest('[name="checkout"], a[href*="/checkout"], .additional-checkout-buttons');
    if (checkout && (cartService.busy || cartService.error)) {
      event.preventDefault(); event.stopImmediatePropagation();
      showNotice(cartService.busy ? 'Updating gift wrapping. Please try checkout again in a moment.' : 'Gift wrapping could not be updated. Please retry before checkout.');
    }
  }
  document.addEventListener('click', guardCheckout, true);
  document.addEventListener('submit', guardCheckout, true);
  synchronize();
})(typeof window !== 'undefined' ? window : globalThis);
